import { apiClient } from "./apiClient";
import { logger } from "./logger";

export interface Plan {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  limit?: {
    daily_trade_cap: number;
  };
}

export interface PlanPrice {
  plan_id: string;
  period: "monthly" | "yearly";
  price_usd: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateOrUpdatePlanPriceRequest {
  period: "monthly" | "yearly";
  price_usd: number;
  is_active?: boolean;
}

export interface UpdatePlanLimitRequest {
  daily_trade_cap: number;
}

export interface AssignSubscriptionRequest {
  user_id: string;
  plan_code: "STANDARD" | "PRO";
  period_days?: number;
}

export interface ExtendSubscriptionRequest {
  subscription_id: string;
  period_days: number;
}

export interface Subscription {
  id: string;
  user_id: string;
  user_email?: string;
  plan_id: string;
  plan_code: string;
  plan_name: string;
  status: "active" | "paused" | "canceled" | "expired";
  starts_at: string;
  ends_at?: string;
  created_at: string;
  updated_at: string;
}

export async function listPlans(): Promise<Plan[]> {
  try {
    const response = await apiClient.get<Plan[]>("/admin/plans");
    return response.data;
  } catch (error) {
    logger.error("Error listing plans:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updatePlanLimit(
  planId: string,
  data: UpdatePlanLimitRequest,
): Promise<{ daily_trade_cap: number }> {
  try {
    const response = await apiClient.patch<{ daily_trade_cap: number }>(
      `/admin/plans/${planId}/limits`,
      data,
    );
    return response.data;
  } catch (error) {
    logger.error(`Error updating plan ${planId} limit:`, error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function assignSubscription(
  data: AssignSubscriptionRequest,
): Promise<Subscription> {
  try {
    const response = await apiClient.post<Subscription>(
      "/admin/plans/subscriptions/assign",
      data,
    );
    return response.data;
  } catch (error) {
    logger.error("Error assigning subscription:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function listSubscriptions(params?: {
  user_id?: string;
  plan_id?: string;
  skip?: number;
  limit?: number;
}): Promise<Subscription[]> {
  try {
    const response = await apiClient.get<Subscription[]>("/admin/plans/subscriptions", {
      params,
    });
    return response.data;
  } catch (error) {
    logger.error("Error listing subscriptions:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function listPlanPrices(planId: string): Promise<PlanPrice[]> {
  try {
    const response = await apiClient.get<PlanPrice[]>(`/admin/plans/${planId}/prices`);
    return response.data;
  } catch (error) {
    logger.error(`Error listing plan prices for ${planId}:`, error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function createOrUpdatePlanPrice(
  planId: string,
  data: CreateOrUpdatePlanPriceRequest,
): Promise<PlanPrice> {
  try {
    const response = await apiClient.post<PlanPrice>(
      `/admin/plans/${planId}/prices`,
      data,
    );
    return response.data;
  } catch (error) {
    logger.error(`Error creating/updating plan price for ${planId}:`, error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function extendSubscription(
  data: ExtendSubscriptionRequest,
): Promise<Subscription> {
  try {
    const response = await apiClient.post<Subscription>(
      "/admin/plans/subscriptions/extend",
      data,
    );
    return response.data;
  } catch (error) {
    logger.error("Error extending subscription:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

